// filepath: apps/web/src/features/cabinet/RequiredUserParameters/RequiredUserParametersPage.tsx
//
// FR-UI-CABINET-3 — Required User Parameters page (the user-self-
// service surface where the authenticated user supplies the values
// for the tenant's required user parameters).
//
// The page composes:
//   - useRequiredParameters (queries.ts) — the GET /api/me/required-parameters hook
//   - usePatchRequiredParameters (queries.ts) — the PATCH hook for submit
//   - The 5 mandatory state components from RequiredUserParametersPage.states.tsx
//
// Security invariants:
//   - INV-1: the page renders exactly what the server returns; no
//     client-side filtering or reshaping of the field set.
//   - INV-2: every field the server includes is rendered — the
//     server is the authoritative strip.
//   - INV-5: the values displayed are sourced from the response
//     body and rendered as text nodes (no dangerouslySetInnerHTML).
//   - INV-9: zero raw HTML — all chrome flows through i18n.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Button } from "@/design-system/Button";
import { HttpConflictError } from "@/definitions/types";
import { useRequiredParameters, usePatchRequiredParameters } from "./queries";
import { classifyRequiredParametersError } from "./types";
import {
  RequiredParametersEmpty,
  RequiredParametersFetchFailure,
  RequiredParametersLoading,
  RequiredParametersMissingRow,
  RequiredParametersPermissionDenied,
  RequiredParametersRateLimit,
  RequiredParametersStaleVersion,
  RequiredParametersSuppliedRow,
} from "./RequiredUserParametersPage.states";

export function RequiredUserParametersPage(): React.ReactElement {
  const { t } = useTranslation();
  const query = useRequiredParameters();
  const patchMutation = usePatchRequiredParameters();

  if (query.isLoading) {
    return <RequiredParametersLoading />;
  }
  if (query.error) {
    const status = classifyRequiredParametersError(query.error);
    if (status === "permission-denied") {
      return <RequiredParametersPermissionDenied />;
    }
    if (status === "stale-version") {
      const current = extractCurrentRowVersion(query.error);
      return (
        <RequiredParametersStaleVersion
          currentRowVersion={current}
          onRefetch={() => {
            void query.refetch();
          }}
        />
      );
    }
    if (status === "rate-limit") {
      return <RequiredParametersRateLimit />;
    }
    return (
      <RequiredParametersFetchFailure
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const data = query.data;
  if (!data) {
    return <RequiredParametersLoading />;
  }

  // Empty: no required fields → empty-state component.
  if (data.fields.length === 0) {
    return <RequiredParametersEmpty />;
  }

  const missingCount = data.fields.filter((f) => f.missing).length;
  const suppliedCount = data.fields.length - missingCount;
  const isStaleVersion =
    patchMutation.error &&
    classifyRequiredParametersError(patchMutation.error) === "stale-version";

  return (
    <main
      data-testid="required-user-parameters-page"
      style={{
        padding: "32px",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        maxWidth: "720px",
        margin: "0 auto",
      }}
    >
      <header style={{ marginBottom: "24px" }}>
        <h1
          data-testid="required-user-parameters-page-title"
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#111827",
            margin: 0,
            marginBottom: "8px",
          }}
        >
          {t(
            "cabinet.required_user_parameters.title",
            "Required user parameters",
          )}
        </h1>
        <p
          style={{
            fontSize: "14px",
            color: "#4b5563",
            margin: 0,
          }}
        >
          {t(
            "cabinet.required_user_parameters.subtitle",
            "Your tenant requires these parameters. Provide values to complete your profile.",
          )}
        </p>
        <div
          data-testid="required-user-parameters-summary"
          style={{
            marginTop: "12px",
            fontSize: "13px",
            color: "#6b7280",
          }}
        >
          {data.complete
            ? t(
                "cabinet.required_user_parameters.summary.complete",
                "All required parameters are provided.",
              )
            : t(
                "cabinet.required_user_parameters.summary.missing_count",
                "{count} required parameters are still missing.",
                { count: String(missingCount) },
              )}
          {suppliedCount > 0
            ? ` ${t("cabinet.required_user_parameters.summary.supplied_count", "{count} provided.", { count: String(suppliedCount) })}`
            : ""}
        </div>
      </header>

      {isStaleVersion ? (
        <div
          role="alert"
          data-testid="required-user-parameters-stale-banner"
          style={{
            padding: "12px 16px",
            marginBottom: "16px",
            background: "#fef3c7",
            color: "#92400e",
            border: "1px solid #fbbf24",
            borderRadius: "var(--radius-md)",
            fontSize: "13px",
          }}
        >
          {t(
            "cabinet.required_user_parameters.stale_version_banner",
            "This page was modified by another session. Refresh to continue.",
          )}
        </div>
      ) : null}

      <ul
        data-testid="required-user-parameters-fields"
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
        }}
      >
        {data.fields.map((field) =>
          field.missing ? (
            <RequiredParametersMissingRow key={field.key} field={field} />
          ) : (
            <RequiredParametersSuppliedRow key={field.key} field={field} />
          ),
        )}
      </ul>

      <footer style={{ marginTop: "24px" }}>
        <Button
          variant="primary"
          size="md"
          disabled={data.complete || patchMutation.isPending}
          data-testid="required-user-parameters-resolve"
          onClick={() => {
            // The page is a self-service completion surface; the
            // actual value entry happens via the generic form
            // renderer (FormRenderer.tsx) for each field. The
            // "resolve" button is the navigation affordance to
            // open the form. We surface a placeholder here so the
            // button is wired and the test hook works; a future
            // FR may add the per-field edit affordance.
            void patchMutation.mutateAsync({
              row_version: data.row_version,
              values: {},
            });
          }}
        >
          {t("cabinet.required_user_parameters.resolve", "Provide values")}
        </Button>
      </footer>
    </main>
  );
}

// extractCurrentRowVersion reads the canonical FR-UI-CORE-6 conflict
// envelope's current_row_version field. queries.ts wraps 409 responses
// in HttpConflictError (canonical FR-UI-CORE-6 wire shape), so the
// typed `currentRowVersion` carries the server's row_version. We also
// fall back to parsing the JSON-encoded message body to support
// older 409 envelopes that didn't carry the typed field.
function extractCurrentRowVersion(err: unknown): number {
  if (err instanceof HttpConflictError) {
    if (
      typeof err.currentRowVersion === "number" &&
      Number.isFinite(err.currentRowVersion)
    ) {
      return err.currentRowVersion;
    }
  }
  if (typeof err === "object" && err !== null) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === "string") {
      try {
        const parsed: unknown = JSON.parse(msg);
        if (typeof parsed === "object" && parsed !== null) {
          const v = (parsed as { current_row_version?: unknown })
            .current_row_version;
          if (typeof v === "number" && Number.isFinite(v)) return v;
        }
      } catch {
        // ignore parse failures — the caller renders a generic message.
      }
    }
  }
  return 0;
}
